import type { IntlShape } from 'react-intl';

import { EntityState } from 'src/apiClients/queries';
import type { DataTableCellValue, FilterOption } from 'src/components/data-table/DataTable.types';
import type {
    GlobalSearchFilters,
    TopBarSearchEntityType,
} from 'src/components/top-bar/top-bar-search/entity-types';
import { TOP_BAR_SEARCH_GROUP_ORDER } from 'src/components/top-bar/top-bar-search/entity-types';
import {
    createOwnerFilterOptions,
    createUserTypeFilterOptions,
} from 'src/pages/users-page/user-table-formatters';
import {
    createAccessFilterOptions,
    createStateFilterOptions,
    ADDON_PRODUCT_NAMES,
} from 'src/utils/table-formatters';

import { registryI18n } from './entity-filter-registry.i18n';

export type { TopBarSearchEntityType };

/** The shared resolved shape consumed by both page filter columns and global search groups. */
export interface ResolvedFilterColumn {
    field: string;
    headerName: string;
    filterOptions: FilterOption[];
}

type FilterScope = 'page' | 'global' | 'both';

interface EntityFilterField {
    field: string;
    label: (intl: IntlShape) => string;
    createOptions: (intl: IntlShape) => FilterOption[];
    defaultSelection?: DataTableCellValue[];
    scope: FilterScope;
}

const STATE_FIELD: EntityFilterField = {
    field: 'state',
    label: (intl) => intl.formatMessage(registryI18n.statusHeader),
    createOptions: createStateFilterOptions,
    defaultSelection: [EntityState.ACTIVE, EntityState.INACTIVE],
    scope: 'both',
};

const PRODUCT_CODE_FIELD: EntityFilterField = {
    field: 'productCode',
    label: (intl) => intl.formatMessage(registryI18n.productCodeHeader),
    createOptions: () =>
        Object.keys(ADDON_PRODUCT_NAMES).map((code) => ({ label: code, value: code })),
    scope: 'global',
};

const PRODUCT_NAME_FIELD: EntityFilterField = {
    field: 'name',
    label: (intl) => intl.formatMessage(registryI18n.productNameHeader),
    createOptions: () =>
        Object.values(ADDON_PRODUCT_NAMES).map((name) => ({ label: name, value: name })),
    scope: 'global',
};

const USER_TYPE_FIELD: EntityFilterField = {
    field: 'type',
    label: (intl) => intl.formatMessage(registryI18n.typeHeader),
    createOptions: createUserTypeFilterOptions,
    scope: 'both',
};

const OWNER_FIELD: EntityFilterField = {
    field: 'isSubscriptionOwner',
    label: (intl) => intl.formatMessage(registryI18n.ownerHeader),
    createOptions: createOwnerFilterOptions,
    scope: 'both',
};

const ACCESS_FIELD: EntityFilterField = {
    field: 'access',
    label: (intl) => intl.formatMessage(registryI18n.accessHeader),
    createOptions: createAccessFilterOptions,
    scope: 'both',
};

const REGISTRY: Record<TopBarSearchEntityType, EntityFilterField[]> = {
    instance: [STATE_FIELD, PRODUCT_CODE_FIELD, PRODUCT_NAME_FIELD],
    user: [STATE_FIELD, USER_TYPE_FIELD, OWNER_FIELD],
    addon: [STATE_FIELD],
    workspace: [STATE_FIELD, ACCESS_FIELD],
    dataset: [STATE_FIELD],
    supportPackage: [STATE_FIELD],
};

/**
 * Resolves the filter columns for a given entity type and scope.
 * Pass `scope: 'global'` for the top-bar search; `scope: 'page'` for page tables;
 * omit (defaults to `'both'`) to get all fields regardless of scope.
 */
export const resolveEntityFilters = (
    type: TopBarSearchEntityType,
    intl: IntlShape,
    scope: FilterScope = 'both',
): ResolvedFilterColumn[] =>
    REGISTRY[type]
        .filter((f) => scope === 'both' || f.scope === 'both' || f.scope === scope)
        .map((f) => ({
            field: f.field,
            headerName: f.label(intl),
            filterOptions: f.createOptions(intl),
        }));

/** Returns the canonical default filter selections for a given entity type. */
export const defaultFiltersFor = (
    type: TopBarSearchEntityType,
): Record<string, DataTableCellValue[]> =>
    Object.fromEntries(
        REGISTRY[type]
            .filter((f) => f.defaultSelection !== undefined)
            .map((f) => [f.field, f.defaultSelection!]),
    );

/** Returns the initial GlobalSearchFilters state for TopBarSearch, seeded from registry defaults. */
export const defaultGlobalFilters = (): GlobalSearchFilters =>
    Object.fromEntries(
        TOP_BAR_SEARCH_GROUP_ORDER.flatMap((type) =>
            REGISTRY[type]
                .filter((f) => f.defaultSelection !== undefined && f.scope !== 'page')
                .map((f) => [`${type}.${f.field}`, f.defaultSelection!]),
        ),
    );
